import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getPriceShape, formatCHF } from '../utils/priceUtils';

/**
 * Canonical price renderer used everywhere in the admin app.
 *
 * Pass it any article-like object (article, product, price object, scalar) and
 * it figures out the right thing to render: current price, optional
 * strike-through base price, optional percentage badge, or a fallback string
 * when no price is known.
 *
 * Variants:
 *   <PriceDisplay article={a} />                 — default inline layout
 *   <PriceDisplay article={a} size="lg" />       — bigger text (product detail page)
 *   <PriceDisplay article={a} fallbackKey="..."/>— override the i18n key for "no price"
 */
const PriceDisplay = ({
  article,
  size = 'sm',
  fallbackKey = 'products.productCard.noPrice',
  showBadge = true,
  className = '',
}) => {
  const { t } = useLanguage();
  const p = getPriceShape(article);

  const baseTextClass = size === 'lg' ? 'text-base' : 'text-sm';
  const baseFontClass = 'font-medium text-gray-900';

  if (p.currentPrice == null) {
    return (
      <p className={`${baseTextClass} ${baseFontClass} ${className}`}>
        {t(fallbackKey)}
      </p>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className={`${baseTextClass} ${baseFontClass}`}>
        {p.currency} {formatCHF(p.currentPrice)}
      </span>
      {p.inDiscount && p.basePrice != null && (
        <>
          <span className={`${baseTextClass} text-gray-500 line-through`}>
            {p.currency} {formatCHF(p.basePrice)}
          </span>
          {showBadge && p.discountPercentage != null && (
            <span className="text-xs font-medium text-[#E05257] bg-[#E05257]/10 px-2 py-0.5 rounded-full">
              -{p.discountPercentage}%
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default PriceDisplay;
